import type { Booking, BookingPayment } from '@/api/bookings';
import type { TaxDocument } from '@/api/taxDocuments';
import { useBookingTaxDocuments } from '@/api/taxDocuments';
import { useEscapeKey } from '@/components/shortcuts';
import { paymentKindLabel, paymentMethodLabel } from '@core/bookings/paymentWords';
import type { StageMove } from '@core/bookings/stageMoves';
import { formatDate } from '@core/format/dates';
import { formatAmount, formatSigned } from '@core/format/money';
import { documentTitle } from '@core/invoices/register';

import { MoneyLine, MoneyLines } from './MoneyLine';

/**
 * The booking's ··· menu on the website — the phone's `BookingMenu`: everything that is not the next
 * step. The money as it stands, printing, each invoice and receipt, and the ways out (keep it longer,
 * cancel, expire, recovery), so Cancel is never beside the big button.
 *
 * <p>
 * <strong>Every row is offered only where it can be used</strong>, decided by the page: printing
 * under `RentalOrder:Print`, invoices under Billing with `SalesInvoice:View`, the ways out only as the
 * shared stage rule allows. A side panel, like the invoice drawer it opens.
 * </p>
 */
export function BookingMenu({
  booking,
  currency,
  prints,
  readsInvoices,
  hasDocket,
  hasCard,
  exits,
  mayExtend,
  onPrintCopy,
  onPrintDockets,
  onPrintCard,
  onPrintReceipt,
  onOpenInvoice,
  onExtend,
  onExit,
  onClose,
}: {
  booking: Booking;
  currency: string;
  prints: boolean;
  readsInvoices: boolean;
  hasDocket: boolean;
  /** Garments with changes noted — the fitting card has something to print. */
  hasCard: boolean;
  /** Cancel, expire and recovery — the stage moves of kind `exit` this reader may make. */
  exits: readonly StageMove[];
  mayExtend: boolean;
  onPrintCopy: () => void;
  onPrintDockets: () => void;
  onPrintCard: () => void;
  onPrintReceipt: (payment: BookingPayment) => void;
  onOpenInvoice: (document: TaxDocument) => void;
  onExtend: () => void;
  onExit: (move: StageMove) => void;
  onClose: () => void;
}) {
  const cash = (amount: number | null) => `${currency}${formatAmount(amount)}`;
  const documents = useBookingTaxDocuments(readsInvoices ? booking.id : undefined);

  useEscapeKey(onClose);

  /** Closes the menu, then opens what was asked for. */
  const go = (open: () => void) => {
    onClose();
    open();
  };

  return (
    <div
      data-shortcut-layer
      className="fixed inset-0 z-9999 flex justify-end print:hidden"
      role="dialog"
      aria-modal="true"
      aria-label={`More for ${booking.documentNumber || 'this booking'}`}
    >
      <button
        type="button"
        aria-label="Close the menu"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-[var(--overlay-scrim)]"
      />

      <div className="relative flex h-full w-[420px] flex-col border-l border-[var(--overlay-border)] bg-[var(--overlay-surface)] shadow-lg max-sm:w-full">
        <header className="border-border-color flex items-start justify-between gap-3 border-b p-4">
          <div className="min-w-0">
            <p className="font-mono-nums text-sm font-semibold text-gray-900">
              {booking.documentNumber || 'No number yet'}
            </p>
            <p className="text-default truncate text-[12px]">{booking.customerName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-default flex size-7 cursor-pointer items-center justify-center rounded hover:bg-light"
          >
            <i className="icon icon-x" />
          </button>
        </header>

        <div className="flex flex-col gap-5 overflow-y-auto p-4">
          <section>
            <Heading>The money as it stands</Heading>
            <div className="border-border-color rounded border bg-light p-3">
              <MoneyLines>
                <MoneyLine label="Invoice total" value={cash(booking.invoiceTotal)} />
                <MoneyLine label="Received" value={cash(booking.paidAmount)} />
                <MoneyLine
                  label="Balance"
                  value={cash(booking.balanceDue)}
                  tone={booking.balanceDue > 0 ? 'warning' : undefined}
                  strong
                />
                <MoneyLine
                  label="Deposit"
                  value={cash(booking.deposit.amount ?? 0)}
                  hint={booking.deposit.state === 'NotTaken' ? 'not taken yet' : undefined}
                  tone="brand"
                />
              </MoneyLines>
            </div>
          </section>

          {prints && (
            <section>
              <Heading>Print</Heading>
              <Row
                title="The money as it stands"
                sub="What is paid and what is owed — Alt+P"
                onClick={() => go(onPrintCopy)}
              />
              {hasCard && (
                <Row
                  title="Fitting card"
                  sub="Each garment's changes, in the shop's own boxes"
                  onClick={() => go(onPrintCard)}
                />
              )}
              {hasDocket && (
                <Row
                  title="All dockets"
                  sub="Every garment with tailor work, one sheet each"
                  onClick={() => go(onPrintDockets)}
                />
              )}
            </section>
          )}

          {readsInvoices && (documents.data?.length ?? 0) > 0 && (
            <section>
              <Heading>Invoices</Heading>
              {(documents.data ?? []).map((document) => (
                <Row
                  key={document.id}
                  title={`${documentTitle(document)}${document.status === 'Cancelled' ? ' · cancelled' : ''}`}
                  sub={document.documentNumber}
                  value={cash(document.documentTotal)}
                  onClick={() => go(() => onOpenInvoice(document))}
                />
              ))}
            </section>
          )}

          <section>
            <Heading>Payments and receipts</Heading>
            {booking.payments.length === 0 ? (
              <p className="text-default text-[12.5px]">Nothing received yet.</p>
            ) : (
              booking.payments.map((payment) => (
                <Row
                  key={payment.id}
                  title={paymentKindLabel[payment.kind]}
                  sub={`${payment.receiptNumber} · ${formatDate(payment.receivedOn)} · ${paymentMethodLabel[payment.method]}`}
                  // Signed, so a refund reads as one rather than looking like a receipt.
                  value={`${currency}${formatSigned(payment.amount)}`}
                  onClick={prints ? () => go(() => onPrintReceipt(payment)) : undefined}
                />
              ))
            )}
          </section>

          {(mayExtend || exits.length > 0) && (
            <section>
              <Heading>Change the booking</Heading>
              {mayExtend && (
                <Row
                  title="Keep it longer"
                  sub="Move the return day; the extra days are charged per day"
                  onClick={() => go(onExtend)}
                />
              )}
              {exits.map((move) => (
                <Row
                  key={move.to}
                  title={move.label}
                  danger={move.to === 'Cancelled'}
                  onClick={() => go(() => onExit(move))}
                />
              ))}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function Heading({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-default mb-1.5 text-[11.5px] font-semibold tracking-wide uppercase">
      {children}
    </p>
  );
}

function Row({
  title,
  sub,
  value,
  danger = false,
  onClick,
}: {
  title: string;
  sub?: string;
  value?: string;
  danger?: boolean;
  /** Absent draws the row as a plain line — a receipt this reader may not print. */
  onClick?: (() => void) | undefined;
}) {
  return (
    <button
      type="button"
      disabled={!onClick}
      onClick={onClick}
      className="border-border-color -mx-2 flex w-[calc(100%+1rem)] cursor-pointer items-center gap-3 rounded border-t px-2 py-2.5 text-start first-of-type:border-t-0 hover:bg-light disabled:cursor-default disabled:hover:bg-transparent"
    >
      <span className="min-w-0 flex-1">
        <span
          className={`block text-[13px] font-semibold ${danger ? 'text-danger' : 'text-gray-900'}`}
        >
          {title}
        </span>
        {sub && <span className="text-default block text-[11.5px]">{sub}</span>}
      </span>
      {value && <span className="font-mono-nums text-[13px] text-gray-900">{value}</span>}
      {onClick && <i className="icon icon-chevron-right text-default text-[12px]" />}
    </button>
  );
}
